import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    DocumentTextIcon,
    PaperClipIcon,
} from '@heroicons/react/24/outline';
import { DrawerCustom } from '../../../components/drawer/Drawer';
import { Button } from '../../../components/buttons/Button';
import { InputTextModern } from '../../../components/inputs/InputTextModern';
import { TextareaModern } from '../../../components/textareaModern';
import { TaskAttachmentField } from '../tasks/TaskAttachmentField';
import { useResourceUploads } from '../../../hooks/useResourceUploads';
import { createCourseResources } from '../../../redux/actions/resources';
import {
    COURSE_RESOURCE_LIMIT,
    RESOURCE_UPLOAD_ACCEPT,
    RESOURCE_UPLOAD_HINT,
} from '../../../common/resourceUploads';
import { CourseFormSection } from '../components/CourseFormSection';
import '../tasks/task-form.css';

const SECTION_ICON_CLASS = 'h-5 w-5';

export const ResourceFormDrawer = ({
    isOpen,
    onClose,
    courseId,
    remaining = COURSE_RESOURCE_LIMIT,
    onCreated,
}) => {
    const dispatch = useDispatch();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [fieldsKey, setFieldsKey] = useState(0);
    const draftEntityKeyRef = useRef(null);

    const draftEntityKey = `create:${courseId || 'new'}`;

    const {
        items,
        readyPayloads,
        isUploading,
        hasErrors,
        startUploads,
        removeItem,
        clearAll,
    } = useResourceUploads(courseId, { maxFiles: remaining });

    const isProcessing = saving || isUploading;

    const resetDraft = useCallback(() => {
        draftEntityKeyRef.current = null;
        setTitle('');
        setDescription('');
        setSaving(false);
        clearAll();
    }, [clearAll]);

    useEffect(() => {
        if (!isOpen) return;

        setFieldsKey((key) => key + 1);

        if (draftEntityKeyRef.current === draftEntityKey) {
            return;
        }

        draftEntityKeyRef.current = draftEntityKey;
        setTitle('');
        setDescription('');
        setSaving(false);
        clearAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, draftEntityKey]);

    const handleSelectFiles = useCallback(async (files) => {
        const result = await startUploads(files, { remainingSlots: remaining });
        if (!result.ok && result.message) {
            toast.error(result.message);
        }
    }, [startUploads, remaining]);

    const handleDismiss = () => {
        if (isProcessing) return;
        onClose({ reset: false });
    };

    const handleCancel = () => {
        if (isProcessing) return;
        resetDraft();
        onClose({ reset: true });
    };

    const handleSave = async () => {
        if (saving || isUploading) return;
        if (!readyPayloads.length) {
            toast.error('Sube al menos un archivo para crear el recurso.');
            return;
        }
        if (readyPayloads.length > remaining) {
            toast.error(`Solo puedes subir ${remaining} archivo${remaining === 1 ? '' : 's'} más.`);
            return;
        }

        setSaving(true);
        try {
            const data = await dispatch(createCourseResources(courseId, {
                title: title.trim(),
                description: description.trim(),
                files: readyPayloads,
            }));
            if (data?.errors?.length) {
                toast.error(data.errors[0].message);
            } else {
                toast.success(data.message || 'Recursos subidos');
            }
            resetDraft();
            onCreated?.(data);
            onClose({ reset: true });
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudieron subir los recursos.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DrawerCustom
            isOpen={isOpen}
            toggleDrawer={handleDismiss}
            lockClose={isProcessing}
            title="Nuevo recurso"
            subtitle={`${remaining} ${remaining === 1 ? 'espacio disponible' : 'espacios disponibles'}`}
            footer={(
                <div className="task-form__footer-actions">
                    <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        className="task-form__submit"
                        onClick={handleSave}
                        loading={saving}
                        loadingLabel="Subiendo…"
                        disabled={isUploading || !readyPayloads.length}
                    >
                        Subir recursos
                    </Button>
                </div>
            )}
        >
            <div className="task-form">
                <div className="task-form__scroll">
                    <div key={fieldsKey} className="task-form__fields">
                        <CourseFormSection
                            id="resource-section-details"
                            title="Detalles"
                            description="Título y descripción opcionales del material."
                            icon={<DocumentTextIcon className={SECTION_ICON_CLASS} />}
                        >
                            <InputTextModern
                                id="resource-title"
                                name="resource-title"
                                label="Título (opcional)"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Si lo dejas vacío, usamos el nombre del archivo"
                                autoComplete="off"
                            />
                            <TextareaModern
                                id="resource-description"
                                name="resource-description"
                                label="Descripción (opcional)"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Material de estudio, ejercicios, presentaciones…"
                                maxLength={1000}
                                minRows={3}
                            />
                        </CourseFormSection>

                        <CourseFormSection
                            id="resource-section-files"
                            title="Archivos"
                            description="Sube el material que verán los alumnos del curso."
                            icon={<PaperClipIcon className={SECTION_ICON_CLASS} />}
                        >
                            <TaskAttachmentField
                                label="Archivos"
                                staged={items}
                                isUploading={isUploading}
                                onSelectFiles={handleSelectFiles}
                                onRemoveStaged={removeItem}
                                disabled={saving || remaining <= 0}
                                multiple
                                maxFiles={remaining}
                                accept={RESOURCE_UPLOAD_ACCEPT}
                                hint={RESOURCE_UPLOAD_HINT}
                            />
                            {hasErrors ? (
                                <p className="task-form__field-error" role="alert">
                                    Quita los archivos con error o corrige el formato y el tamaño.
                                </p>
                            ) : null}
                        </CourseFormSection>
                    </div>
                </div>
            </div>
        </DrawerCustom>
    );
};
